import type { ReactNode } from "react";

import { cn } from "@/lib/utils";
import { DashboardWidget, EmptyChart } from "./record-dashboard-shell";

/*
 * The list-shaped dashboard widgets at rest, as markup: a record list (short
 * summaries on a phone, a table from sm up) and a text list of insights or
 * risks. The widgets keep the table state and field formatting and render
 * through these, so fixture rows compose the same widget.
 */

/** A text list's one column in its table presentation. A record list's table sizes its record column as TITLE_COLUMN_SIZE and its fields by fieldColumnSize. */
export const DASHBOARD_TEXT_COLUMN_SIZE = {
  size: 320,
  minSize: 160,
  maxSize: 720,
} as const;

/** A record as its phone summary shows it: the title and its first three columns. */
export type DashboardRecordSummary = {
  id: string;
  /** The record's title, or the start of its id when it has none. */
  title: string;
  /** Hover text for the title: the full title, or the record id. */
  titleHint: string;
  cells: readonly DashboardRecordSummaryCell[];
  onOpen?: () => void;
};

export type DashboardRecordSummaryCell = {
  key: string;
  label: string;
  /** The value as text, for hover. */
  hint: string;
  /** Numbers, amounts and percentages align right. */
  numeric?: boolean;
  value: ReactNode;
};

/** A record list widget: phone summaries below sm, the table from sm up, or a note that nothing matches. */
export function DashboardRecordListView({
  title,
  description,
  meta,
  actions,
  records,
  openable = false,
  table,
}: {
  title: string;
  description?: string;
  meta?: string;
  actions?: ReactNode;
  records: readonly DashboardRecordSummary[];
  /** Titles open their record, as buttons. */
  openable?: boolean;
  /** The table from sm up. */
  table?: ReactNode;
}) {
  return (
    <DashboardWidget
      title={title}
      description={description}
      meta={meta}
      actions={actions}
      className="h-full bg-background"
    >
      {records.length ? (
        <>
          <div className="min-h-0 flex-1 divide-y divide-border/50 overflow-auto rounded-md border border-border/60 scrollbar-thin sm:hidden">
            {records.map((record) => (
              <article key={record.id} className="px-3 py-2.5">
                <h3
                  className="min-w-0 text-xs font-semibold text-foreground"
                  title={record.titleHint}
                >
                  <DashboardRecordTitleView
                    title={record.title}
                    openable={openable}
                    onOpen={record.onOpen}
                  />
                </h3>
                <dl className="mt-1.5 grid grid-cols-[minmax(0,1fr)_minmax(0,1.35fr)] gap-x-3 gap-y-1 text-2xs leading-4">
                  {record.cells.map((cell) => (
                    <div key={cell.key} className="contents">
                      <dt
                        className="truncate text-muted-foreground"
                        title={cell.label}
                      >
                        {cell.label}
                      </dt>
                      <dd
                        className={cn(
                          "truncate text-foreground tabular-nums",
                          cellAlign(cell.numeric),
                        )}
                        title={cell.hint}
                      >
                        {cell.value}
                      </dd>
                    </div>
                  ))}
                </dl>
              </article>
            ))}
          </div>
          <div className="hidden min-h-0 flex-1 sm:flex">{table}</div>
        </>
      ) : (
        <EmptyChart title="No matching records">
          Adjust the dashboard filters or add records to this channel.
        </EmptyChart>
      )}
    </DashboardWidget>
  );
}

/** A record's title, a button that opens the record where the list allows it. */
export function DashboardRecordTitleView({
  title,
  openable = false,
  onOpen,
}: {
  title: string;
  openable?: boolean;
  onOpen?: () => void;
}) {
  if (!openable && !onOpen) return <span className="block truncate">{title}</span>;
  return (
    <button
      type="button"
      className="block max-w-full truncate rounded-sm text-left hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
      onClick={onOpen}
    >
      {title}
    </button>
  );
}

/** The record column's cell in the table. */
export function DashboardRecordTitleCellView({
  title,
  titleHint,
  openable,
  onOpen,
}: {
  title: string;
  titleHint: string;
  openable?: boolean;
  onOpen?: () => void;
}) {
  return (
    <div className="truncate font-medium text-foreground" title={titleHint}>
      <DashboardRecordTitleView title={title} openable={openable} onOpen={onOpen} />
    </div>
  );
}

/** A field's cell in the table: the value, its text on hover, numbers to the right. */
export function DashboardRecordValueCellView({
  hint,
  numeric = false,
  children,
}: {
  hint: string;
  numeric?: boolean;
  children?: ReactNode;
}) {
  return (
    <span
      className={cn(
        "block truncate tabular-nums text-foreground",
        cellAlign(numeric),
      )}
      title={hint}
    >
      {children}
    </span>
  );
}

/** An insight or risk list widget: bullets, or the table a table presentation passes. */
export function DashboardTextListView({
  title,
  description,
  meta,
  actions,
  items = [],
  tone,
  table,
}: {
  title: string;
  description?: string;
  meta?: string;
  actions?: ReactNode;
  items?: readonly string[];
  tone: "insight" | "risk";
  /** A table presentation's grid, shown in place of the bullets. */
  table?: ReactNode;
}) {
  return (
    <DashboardWidget
      title={title}
      description={description}
      meta={meta}
      actions={actions}
      className="h-full"
    >
      {table ? (
        <div className="flex min-h-0 flex-1 overflow-hidden rounded-md border border-border/60">
          {table}
        </div>
      ) : (
        <ul
          className={cn(
            "space-y-2 rounded-md border px-3 py-2 text-sm",
            tone === "risk"
              ? "border-destructive/30 bg-destructive/5 text-destructive"
              : "border-border/60 bg-muted/20",
          )}
        >
          {items.map((item, index) => (
            <li key={index} className="flex gap-2">
              <span
                className={cn(
                  "mt-2 size-1.5 shrink-0 rounded-full",
                  tone === "risk" ? "bg-destructive" : "bg-[var(--chart-2)]",
                )}
              />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      )}
    </DashboardWidget>
  );
}

/** A text list's cell in its table presentation. */
export function DashboardTextCellView({ value }: { value: string }) {
  return (
    <span className="block truncate text-foreground" title={value}>
      {value}
    </span>
  );
}

function cellAlign(numeric: boolean | undefined): string {
  return numeric ? "text-right" : "text-left";
}
